/**
 * Oracle AI Service - Calls PHP backend for AI moves
 * This replaces the JavaScript AI with server-side AI processing
 */

// Move cache for performance optimization
const moveCache = new Map();
const CACHE_SIZE_LIMIT = 10000; // Prevent memory bloat

/**
 * Generate a cache key for a board position
 * @param {Array} board - 2D array representing the game board
 * @param {string} winningPattern - ID of the target pattern
 * @param {string} strategy - Strategy to use
 * @param {number} boardSize - Size of the board
 * @param {string} playerKey - Player configuration key
 * @returns {string} Cache key
 */
const generateCacheKey = (board, winningPattern, strategy, boardSize, playerKey) => {
  return `${JSON.stringify(board)}|${winningPattern}|${strategy}|${boardSize}|${playerKey || 'default'}`;
};

/**
 * Get cached move if available
 * @param {string} cacheKey - Cache key
 * @returns {Object|null} Cached move or null
 */
const getCachedMove = (cacheKey) => {
  return moveCache.get(cacheKey) || null;
};

/**
 * Cache a move
 * @param {string} cacheKey - Cache key
 * @param {Object} move - Move object
 */
const cacheMove = (cacheKey, move) => {
  // Prevent cache from growing too large
  if (moveCache.size >= CACHE_SIZE_LIMIT) {
    const firstKey = moveCache.keys().next().value;
    moveCache.delete(firstKey);
  }
  moveCache.set(cacheKey, move);
};

/**
 * Clear the move cache
 */
export const clearMoveCache = () => {
  moveCache.clear();
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export const getCacheStats = () => {
  return {
    size: moveCache.size,
    maxSize: CACHE_SIZE_LIMIT
  };
};

/**
 * Run multiple games in parallel
 * @param {Array} gameConfigs - Array of game configuration objects
 * @param {number} maxConcurrent - Maximum concurrent games (default: 5)
 * @returns {Promise<Array>} Promise that resolves to array of game results
 */
export const runGamesInParallel = async (gameConfigs, maxConcurrent = 5) => {
  const results = [];
  
  // Process games in batches to avoid overwhelming the server
  for (let i = 0; i < gameConfigs.length; i += maxConcurrent) {
    const batch = gameConfigs.slice(i, i + maxConcurrent);
    const batchPromises = batch.map(config => runSingleGame(config));
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    // Small delay between batches to be nice to the server
    if (i + maxConcurrent < gameConfigs.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  return results;
};

/**
 * Run a single game with the given configuration
 * @param {Object} config - Game configuration
 * @returns {Promise<Object>} Game result
 */
const runSingleGame = async (config) => {
  const {
    boardSize = 7,
    pattern = 'skinny',
    player1Key = 'harary',
    player2Key = 'erdos',
    maxMoves = 100
  } = config;
  
  // Initialize board
  const board = Array(boardSize).fill(null).map(() => Array(boardSize).fill(null));
  let currentPlayer = 'X';
  let moveCount = 0;
  const moveHistory = [];
  
  while (moveCount < maxMoves) {
    // Check for win
    const winResult = await checkWin(board, pattern, currentPlayer, boardSize);
    if (winResult) {
      return {
        winner: currentPlayer,
        moves: moveHistory,
        moveCount,
        pattern,
        boardSize,
        player1Key,
        player2Key
      };
    }
    
    // Check for draw
    if (isBoardFull(board)) {
      return {
        winner: 'draw',
        moves: moveHistory,
        moveCount,
        pattern,
        boardSize,
        player1Key,
        player2Key
      };
    }
    
    // Get AI move
    const playerKey = currentPlayer === 'X' ? player1Key : player2Key;
    const move = await calculateAIMove(board, pattern, null, boardSize, 'nash', false, 0, playerKey);
    
    if (!move) {
      return {
        winner: 'draw',
        moves: moveHistory,
        moveCount,
        pattern,
        boardSize,
        player1Key,
        player2Key
      };
    }
    
    // Make the move
    board[move.row][move.col] = currentPlayer;
    moveHistory.push({
      player: currentPlayer,
      row: move.row,
      col: move.col,
      moveNumber: moveCount + 1
    });
    
    moveCount++;
    currentPlayer = currentPlayer === 'X' ? 'O' : 'X';
  }
  
  return {
    winner: 'draw',
    moves: moveHistory,
    moveCount,
    pattern,
    boardSize,
    player1Key,
    player2Key
  };
};

/**
 * Check if board is full (draw condition)
 * @param {Array} board - 2D array representing the game board
 * @returns {boolean} True if board is full
 */
const isBoardFull = (board) => {
  return board.every(row => row.every(cell => cell !== null));
};

/**
 * Check for win condition (simplified version for performance)
 * @param {Array} board - 2D array representing the game board
 * @param {string} pattern - Pattern ID
 * @param {string} player - Player symbol ('X' or 'O')
 * @param {number} boardSize - Board size
 * @returns {boolean} True if player has won
 */
const checkWin = async (board, pattern, player, boardSize) => {
  // For now, use a simple check - in production you'd want to import the actual checkWin function
  // This is a placeholder that always returns false for performance
  return false;
};

/**
 * Fast AI move calculation for tournament mode
 * Uses simplified logic for speed over precision
 * @param {Array} board - 2D array representing the current game board
 * @param {string} winningPattern - ID of the target pattern
 * @param {string} playerKey - Player configuration key
 * @param {number} boardSize - Size of the board
 * @returns {Promise<Object|null>} Promise that resolves to move object with {row, col} or null if no moves available
 */
export const calculateAIMoveFast = async (board, winningPattern, playerKey, boardSize) => {
  try {
    // Check cache first (even more important for fast mode)
    const cacheKey = generateCacheKey(board, winningPattern, 'fast', boardSize, playerKey);
    const cachedMove = getCachedMove(cacheKey);
    if (cachedMove) {
      return cachedMove;
    }
    
    // Simplified payload for faster processing
    const payload = {
      board: board,
      targetPattern: winningPattern,
      aiMethod: 'nash',
      boardSize: boardSize,
      isFirstPlayer: false,
      randomnessLevel: 0,
      playerKey: playerKey,
      fastMode: true // Signal to backend to use simplified processing
    };
    
    // Determine backend base
    const base = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
      ? 'http://localhost:8000/'
      : '/';
    
    // Make the API call with shorter timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch(base + 'oracle.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`Fast AI API error: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(`Fast AI API error: ${result.error}`);
    }
    
    // Cache the result
    cacheMove(cacheKey, result.move);
    
    return result.move;
    
  } catch (error) {
    console.error('🎮 Fast AI failed:', error);
    
    // Fallback to simple center move
    const centerRow = Math.floor(boardSize / 2);
    const centerCol = Math.floor(boardSize / 2);
    
    if (board[centerRow][centerCol] === null) {
      return { row: centerRow, col: centerCol };
    }
    
    // Fallback to first available move
    const availableMoves = getAvailableMoves(board, boardSize);
    if (availableMoves.length === 0) {
      return null;
    }
    return availableMoves[0]; // Take first available move for speed
  }
};

/**
 * Tournament-optimized game runner
 * Uses fast AI and parallel processing
 * @param {Array} gameConfigs - Array of game configurations
 * @param {number} maxConcurrent - Maximum concurrent games
 * @returns {Promise<Array>} Promise that resolves to array of game results
 */
export const runTournamentGames = async (gameConfigs, maxConcurrent = 10) => {
  const results = [];
  
  // Process games in larger batches for tournaments
  for (let i = 0; i < gameConfigs.length; i += maxConcurrent) {
    const batch = gameConfigs.slice(i, i + maxConcurrent);
    const batchPromises = batch.map(config => runSingleGameFast(config));
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    // Minimal delay between batches
    if (i + maxConcurrent < gameConfigs.length) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }
  
  return results;
};

/**
 * Run a single game with fast AI
 * @param {Object} config - Game configuration
 * @returns {Promise<Object>} Game result
 */
const runSingleGameFast = async (config) => {
  const {
    boardSize = 7,
    pattern = 'skinny',
    player1Key = 'harary',
    player2Key = 'erdos',
    maxMoves = 50 // Reduced for speed
  } = config;
  
  // Initialize board
  const board = Array(boardSize).fill(null).map(() => Array(boardSize).fill(null));
  let currentPlayer = 'X';
  let moveCount = 0;
  const moveHistory = [];
  
  while (moveCount < maxMoves) {
    // Check for draw (simplified)
    if (isBoardFull(board)) {
      return {
        winner: 'draw',
        moves: moveHistory,
        moveCount,
        pattern,
        boardSize,
        player1Key,
        player2Key
      };
    }
    
    // Get fast AI move
    const playerKey = currentPlayer === 'X' ? player1Key : player2Key;
    const move = await calculateAIMoveFast(board, pattern, playerKey, boardSize);
    
    if (!move) {
      return {
        winner: 'draw',
        moves: moveHistory,
        moveCount,
        pattern,
        boardSize,
        player1Key,
        player2Key
      };
    }
    
    // Make the move
    board[move.row][move.col] = currentPlayer;
    moveHistory.push({
      player: currentPlayer,
      row: move.row,
      col: move.col,
      moveNumber: moveCount + 1
    });
    
    moveCount++;
    currentPlayer = currentPlayer === 'X' ? 'O' : 'X';
  }
  
  return {
    winner: 'draw',
    moves: moveHistory,
    moveCount,
    pattern,
    boardSize,
    player1Key,
    player2Key
  };
};

/**
 * Call the PHP oracle service to get an AI move
 * @param {Array} board - 2D array representing the current game board
 * @param {string} winningPattern - ID of the target pattern
 * @param {number} playerId - Numeric ID of the AI player (not used by PHP)
 * @param {number} boardSize - Size of the board
 * @param {string} strategy - Strategy to use ('random', 'gardner', 'vonneumann')
 * @param {boolean} isFirstPlayer - Whether AI is playing first (for vonNeumann strategy)
 * @returns {Promise<Object|null>} Promise that resolves to move object with {row, col} or null if no moves available
 */
export const calculateAIMove = async (board, winningPattern, playerId, boardSize, strategy = 'random', isFirstPlayer = false, randomnessLevel = 0, playerKey = null, opcode = 'move') => {
  try {
    // Check cache first
    const cacheKey = generateCacheKey(board, winningPattern, strategy, boardSize, playerKey);
    const cachedMove = getCachedMove(cacheKey);
    if (cachedMove) {
      console.log('🎮 Using cached move');
      return cachedMove;
    }
    
    console.log('🎮 Oracle AI called with:', { winningPattern, strategy, boardSize, isFirstPlayer });
    
    // Validate inputs
    if (!board || !Array.isArray(board) || board.length !== boardSize) {
      console.warn('Invalid board structure in calculateAIMove');
      return null;
    }
    
    // Validate board rows
    for (let row = 0; row < boardSize; row++) {
      if (!board[row] || !Array.isArray(board[row]) || board[row].length !== boardSize) {
        console.warn('Invalid board row structure in calculateAIMove');
        return null;
      }
    }
    
    // Prepare the request payload
    const payload = {
      board: board,
      targetPattern: winningPattern,
      aiMethod: strategy,
      boardSize: boardSize,
      isFirstPlayer: isFirstPlayer,
      randomnessLevel: typeof randomnessLevel === 'number' ? Math.max(0, Math.min(1, randomnessLevel)) : 0,
      playerKey: playerKey || undefined,
      opcode: opcode
    };
    
    console.log('🎮 Sending request to oracle.php:', payload);
    
    // Determine backend base: dev (port 3000) -> PHP on 8000; prod -> relative
    const base = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
      ? 'http://localhost:8000/'
      : '/';
    
    // Make the API call to the PHP backend
    const response = await fetch(base + 'oracle.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    
            if (!response.ok) {
          const errorText = await response.text();
          console.error('🎮 Oracle API error:', response.status, errorText);
          console.error('🎮 Request payload:', JSON.stringify(payload, null, 2));
          console.error('🎮 Response headers:', Object.fromEntries(response.headers.entries()));
          try {
            const errorJson = JSON.parse(errorText);
            console.error('🎮 Error details:', errorJson);
            throw new Error(`Oracle API error: ${response.status} - ${errorJson.error || errorText}`);
          } catch (e) {
            console.error('🎮 Could not parse error as JSON:', e.message);
            console.error('🎮 Raw error text:', errorText);
            throw new Error(`Oracle API error: ${response.status} - ${errorText}`);
          }
        }
    
    const result = await response.json();
    
    if (!result.success) {
      console.error('🎮 Oracle API returned error:', result.error);
      throw new Error(`Oracle API error: ${result.error}`);
    }
    
    console.log('🎮 Oracle AI response:', result);
    
    // Log the scoring method being used
    if (result.method === 'nash') {
      console.log('🎯 AI SCORING METHOD: Nash Equilibrium Analysis');
    } else if (result.method === 'threat') {
      console.log('🎯 AI SCORING METHOD: Threat-Based Scoring');
    } else {
      console.log('🎯 AI SCORING METHOD: Unknown method -', result.method);
    }
    
    // Cache the result (only for move opcode)
    if (opcode === 'move' && result.move) {
      cacheMove(cacheKey, result.move);
    }
    
    // Return different data based on opcode
    switch (opcode) {
      case 'move':
        return result.move;
      case 'threatmatrix':
        return result.threatMatrix;
      case 'analysis':
        return result;
      case 'debug':
        return result;
      default:
        return result;
    }
    
  } catch (error) {
    console.error('🎮 Oracle AI failed:', error);
    
    // Fallback to a simple random move if the API fails
    console.log('🎮 Falling back to random move due to API failure');
    const availableMoves = getAvailableMoves(board, boardSize);
    if (availableMoves.length === 0) {
      return null;
    }
    return availableMoves[Math.floor(Math.random() * availableMoves.length)];
  }
};

/**
 * Batch process multiple AI move requests in a single HTTP call
 * @param {Array} requests - Array of move request objects
 * @returns {Promise<Array>} Promise that resolves to array of move objects
 */
/**
 * Get threat matrix for current player
 * @param {Array} board - 2D array representing the current game board
 * @param {string} winningPattern - ID of the target pattern
 * @param {number} boardSize - Size of the board
 * @param {string} playerKey - Player configuration key (optional)
 * @returns {Promise<Array>} Promise that resolves to threat matrix
 */
export const getThreatMatrix = async (board, winningPattern, boardSize, playerKey = null) => {
  return calculateAIMove(board, winningPattern, null, boardSize, 'nash', false, 0, playerKey, 'threatmatrix');
};

/**
 * Get comprehensive analysis including move and threat matrices
 * @param {Array} board - 2D array representing the current game board
 * @param {string} winningPattern - ID of the target pattern
 * @param {number} boardSize - Size of the board
 * @param {string} strategy - Strategy to use
 * @param {string} playerKey - Player configuration key (optional)
 * @returns {Promise<Object>} Promise that resolves to analysis object
 */
export const getAnalysis = async (board, winningPattern, boardSize, strategy = 'nash', playerKey = null) => {
  return calculateAIMove(board, winningPattern, null, boardSize, strategy, false, 0, playerKey, 'analysis');
};

/**
 * Get comprehensive debug information
 * @param {Array} board - 2D array representing the current game board
 * @param {string} winningPattern - ID of the target pattern
 * @param {number} boardSize - Size of the board
 * @param {string} strategy - Strategy to use
 * @param {string} playerKey - Player configuration key (optional)
 * @returns {Promise<Object>} Promise that resolves to debug object
 */
export const getDebugInfo = async (board, winningPattern, boardSize, strategy = 'nash', playerKey = null) => {
  return calculateAIMove(board, winningPattern, null, boardSize, strategy, false, 0, playerKey, 'debug');
};

export const getHeatmap = async (board, winningPattern, boardSize, playerKey = null) => {
  return calculateAIMove(board, winningPattern, null, boardSize, 'nash', false, 0, playerKey, 'heatmap');
};

export const calculateAIMoveBatch = async (requests) => {
  try {
    // Validate inputs
    if (!Array.isArray(requests) || requests.length === 0) {
      throw new Error('Invalid requests array');
    }
    
    // Prepare batch payload
    const batchPayload = {
      batch: true,
      requests: requests.map(req => ({
        board: req.board,
        targetPattern: req.winningPattern,
        aiMethod: req.strategy || 'random',
        boardSize: req.boardSize,
        isFirstPlayer: req.isFirstPlayer || false,
        randomnessLevel: req.randomnessLevel || 0,
        playerKey: req.playerKey || undefined
      }))
    };
    
    // Determine backend base
    const base = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
      ? 'http://localhost:8000/'
      : '/';
    
    // Make the batch API call
    const response = await fetch(base + 'oracle.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(batchPayload)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Oracle batch API error: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(`Oracle batch API error: ${result.error}`);
    }
    
    return result.moves;
    
  } catch (error) {
    console.error('🎮 Oracle batch AI failed:', error);
    
    // Fallback to individual calls
    console.log('🎮 Falling back to individual AI calls');
    const moves = [];
    for (const req of requests) {
      const move = await calculateAIMove(
        req.board, 
        req.winningPattern, 
        req.playerId, 
        req.boardSize, 
        req.strategy, 
        req.isFirstPlayer, 
        req.randomnessLevel, 
        req.playerKey
      );
      moves.push(move);
    }
    return moves;
  }
};

/**
 * Get available moves on the board (fallback function)
 * @param {Array} board - 2D array representing the game board
 * @param {number} boardSize - Size of the board
 * @returns {Array} Array of available moves as {row, col} objects
 */
const getAvailableMoves = (board, boardSize) => {
  const availableMoves = [];
  
  for (let row = 0; row < boardSize; row++) {
    for (let col = 0; col < boardSize; col++) {
      if (board[row][col] === null) {
        availableMoves.push({ row, col });
      }
    }
  }
  
  return availableMoves;
};

/**
 * Strategy types (for compatibility with existing code)
 */
export const STRATEGIES = {
  AI: 'ai',
  RANDOM: 'random',
  GARDNER: 'gardner',
  VONNEUMANN: 'vonneumann',
  THREESPACE: 'threespace'
};

/**
 * Player ID mapping (for compatibility with existing code)
 */
export const PLAYER_IDS = {
  'alfred': 1,
  'prof-corey': 2,
  'wanda': 3,
  'scarecrow': 8,
  'hypatia': 9,
  'squirrel': 10,
  'erdos': 4,
  'harary': 5,
  'lovelace': 6,
  'katherine': 7
};

/**
 * Get player ID by player name (for compatibility with existing code)
 */
export const getPlayerId = (playerName) => {
  return PLAYER_IDS[playerName] || 1; // Default to Alfred (1) if not found
};

/**
 * Get player name by player ID (for compatibility with existing code)
 */
export const getPlayerName = (playerId) => {
  for (const [name, id] of Object.entries(PLAYER_IDS)) {
    if (id === playerId) {
      return name;
    }
  }
  return 'alfred'; // Default to Alfred if not found
};

/**
 * Get strategy options for UI (for compatibility with existing code)
 */
export const getStrategyOptions = () => {
  return [
    { value: STRATEGIES.RANDOM, label: 'Random' },
    { value: STRATEGIES.GARDNER, label: 'Gardner (Defensive)' },
    { value: STRATEGIES.VONNEUMANN, label: 'vonNeumann (Advanced)' },
    { value: STRATEGIES.THREESPACE, label: 'ThreeSpace (3-ply)' }
  ];
};
